import { getDocs, collection, doc, getDoc } from "firebase/firestore";
import { db } from '../../../firebase/config';
import { BRANCH_TYPE_OPTIONS } from '../components/AddBranchModal/service/addBranchService';
import { designationIsIncharge } from "../../../utils/designationRole";

export type Branch = {
    id: string;
    bankId: string;
    branchCode: string;
    address: string;
    mobile: string;
    locationId: string;
    securityCode: string;
    locationName: string;
    psoId?: string;
    psoName: string;
    psoEmail: string;
    psoContact: string;
    name?: string;
    branchManagerId?: string;
    operationalManagerId?: string;
    areaManagerId?: string;
    isActive?: boolean;
    deactivationReason?: string;
    deactivationDate?: string;
    /** Defaults to Branch when missing (legacy records). */
    branchType?: string;
    branchTypeOther?: string;
    cashLimitMillion?: number;
    atmAvailable?: boolean;
    atmMountType?: 'wall_mounted' | 'standalone' | null;
    atmSeparateFromMainEntrance?: boolean | null;
    vantagePointAvailable?: boolean;
    emergencyExitAvailable?: boolean;
    lockerAvailable?: boolean;
    coveredArea?: string;
    landmark?: string;
    nearestOtherBanksBranches?: string;
    nearestPoliceStationName?: string;
    nearestPoliceStationAddress?: string;
    nearestPoliceStationPhone?: string;
    /** Geo JSON: lat, long, optional distanceFromRegion */
    location?: { lat?: number | null; long?: number | null; distanceFromRegion?: string };
    /** Legacy flat fields (older documents) */
    latitudeLongitude?: string;
    distanceFromRegion?: string;
    sraLevel?: 'red' | 'yellow' | 'green' | 'orange' | string;
    sraReason?: string;
    eventAffectedActive?: boolean;
    eventAffectedAreasNote?: string;
    saturdayOpen?: boolean;
    /** Standing guard staffing (permanent roster). */
    numberOfGuardsDay?: number;
    numberOfGuardsNight?: number;
    departments?: string[];
    cdmAvailable?: boolean;
    accessControlAvailable?: boolean;
    accessControlUnits?: number;
    accessControlLocations?: string[];
};

export type Manager = {
    id: string;
    bankId: string;
    name: string;
    email: string;
    mobile: string;
    contact?: string;
    type?: string;
};

/** Display label for KPI / dashboard (aligned with Branches export "Branch type" line). */
export function branchTypeKpiLabel(branch: Pick<Branch, 'branchType' | 'branchTypeOther'>): string {
    const t = String(branch.branchType || 'Branch').trim() || 'Branch';
    if (t === 'Other' && String(branch.branchTypeOther || '').trim()) {
        return `${t} — ${String(branch.branchTypeOther).trim()}`;
    }
    return t;
}

export function countBranchesByType(branches: Pick<Branch, 'branchType' | 'branchTypeOther'>[]): Record<string, number> {
    const byType: Record<string, number> = {};
    for (const b of branches) {
        const label = branchTypeKpiLabel(b);
        byType[label] = (byType[label] || 0) + 1;
    }
    return byType;
}

export type BranchTypeKpiRow = { label: string; count: number };

/**
 * One row per option in Add Branch (including 0). "Other" includes plain Other plus all `Other — …` keys.
 * Any remaining labels in `counts` (e.g. legacy values) are appended at the end.
 */
export function branchTypeKpiOrderedRows(counts: Record<string, number>): BranchTypeKpiRow[] {
    const rows: BranchTypeKpiRow[] = [];
    const consumed = new Set<string>();

    for (const opt of BRANCH_TYPE_OPTIONS) {
        if (opt === 'Other') {
            let c = counts['Other'] || 0;
            for (const k of Object.keys(counts)) {
                if (k.startsWith('Other —')) {
                    c += counts[k] || 0;
                    consumed.add(k);
                }
            }
            consumed.add('Other');
            rows.push({ label: 'Other', count: c });
        } else {
            rows.push({ label: opt, count: counts[opt] || 0 });
            consumed.add(opt);
        }
    }

    const extras = Object.keys(counts)
        .filter((k) => !consumed.has(k))
        .sort((a, b) => a.localeCompare(b))
        .map((k) => ({ label: k, count: counts[k] || 0 }));

    rows.push(...extras);
    return rows;
}

export async function fetchBranchesData(
    uid: string,
    isBankUser: boolean = false,
    psoAuthUid?: string,
    psoDesignation?: string
): Promise<{ branches: Branch[]; managers: Record<string, Manager> }> {
    // Find bankId
    let bankId: string | null = null;

    if (isBankUser) {
        // For bank users, check if uid is a bank document ID or if there's a bank with userId == uid
        const directBank = await getDoc(doc(db, "banks", uid));
        if (directBank.exists()) {
            bankId = uid;
        } else {
            // Try query by userId field
            const banksSnap = await getDocs(collection(db, "banks"));
            for (const bankDoc of banksSnap.docs) {
                const bankData = bankDoc.data();
                if (bankData.userId === uid) {
                    bankId = bankDoc.id;
                    break;
                }
            }
        }
    } else {
        // For PSO users, find which bank has this PSO
        const banksSnap = await getDocs(collection(db, "banks"));
        for (const bankDoc of banksSnap.docs) {
            const posDocRef = doc(db, "banks", bankDoc.id, "pos", uid);
            const posDoc = await getDoc(posDocRef);
            if (posDoc.exists()) {
                bankId = bankDoc.id;
                break;
            }
        }
    }

    if (!bankId) {
        console.log('No bank found for user:', uid);
        return {
            branches: [],
            managers: {},
        };
    }

    // Fetch locations
    const locSnap = await getDocs(collection(db, "locations"));
    const locList = locSnap.docs.map(d => ({ id: d.id, ...d.data() } as { id: string; name: string }));

    // Fetch PSOs from banks/{bankId}/pos
    const psoSnap = await getDocs(collection(db, "banks", bankId, "pos"));
    const psoList = psoSnap.docs.map(d => ({ id: d.id, ...d.data() } as { id: string; name: string; email?: string; contact?: string; psoId?: string }));

    // Fetch managers from banks/{bankId}/managers
    const mgrSnap = await getDocs(collection(db, "banks", bankId, "managers"));
    const managersMap: Record<string, Manager> = {};
    mgrSnap.forEach(mgrDoc => {
        const managerData = { id: mgrDoc.id, bankId: bankId!, ...mgrDoc.data() } as Manager;
        managersMap[mgrDoc.id] = managerData;
        // Also store by any other ID fields that might be in the data
        const data = mgrDoc.data();
        if (data.id && data.id !== mgrDoc.id) {
            managersMap[data.id] = managerData;
        }
    });

    // Fetch bank data to get bank name
    const bankDoc = await getDoc(doc(db, "banks", bankId));
    const bankData = bankDoc.exists() ? bankDoc.data() : {};

    // Get managed PSO IDs for non-bank users.
    // Head-level users can view all branches (view-only access handled in UI).
    let allowedPsoIds: string[] | null = null;
    if (!isBankUser && psoAuthUid && psoDesignation) {
        const normalizedDesignation = psoDesignation.trim().toLowerCase();
        const isHeadLevelUser =
            normalizedDesignation === 'head' ||
            normalizedDesignation === 'head pso' ||
            normalizedDesignation === 'regional head' ||
            normalizedDesignation === 'regional pso';

        // Head users and incharge users can view all branches under hierarchy, so no PSO filter is applied.
        if (normalizedDesignation === 'head' || normalizedDesignation === 'head pso' || designationIsIncharge(psoDesignation)) {
            allowedPsoIds = null;
        } else if (isHeadLevelUser) {
            try {
                // Fetch managed PSOs from /banks/{bankId}/pos/{psoAuthUid}/managedPsos
                const managedSnap = await getDocs(collection(db, 'banks', bankId, 'pos', psoAuthUid, 'managedPsos'));
                const managedPsoIds: string[] = [];
                
                managedSnap.forEach(managedDoc => {
                    const managedData = managedDoc.data();
                    if (managedData.psoId) {
                        managedPsoIds.push(managedData.psoId);
                    }
                });

                // If regional head, include PSOs managed by the regional users under them.
                if (normalizedDesignation === 'regional head' || normalizedDesignation === 'regional pso') {
                    for (const managedPsoId of managedPsoIds) {
                        try {
                            // Find the authUid for this managed PSO
                            const managedPsoDoc = psoList.find(p => p.psoId === managedPsoId);
                            if (managedPsoDoc) {
                                const regionalManagedSnap = await getDocs(
                                    collection(db, 'banks', bankId, 'pos', managedPsoDoc.id, 'managedPsos')
                                );
                                regionalManagedSnap.forEach(regionalManagedDoc => {
                                    const regionalManagedData = regionalManagedDoc.data();
                                    if (regionalManagedData.psoId && !managedPsoIds.includes(regionalManagedData.psoId)) {
                                        managedPsoIds.push(regionalManagedData.psoId);
                                    }
                                });
                            }
                        } catch (error) {
                            console.error('Error fetching Regional PSO managed PSOs:', error);
                        }
                    }
                }

                allowedPsoIds = managedPsoIds;
            } catch (error) {
                console.error('Error fetching managed PSOs:', error);
            }
        } else {
            // Regular PSO - only show branches assigned to this PSO
            // Find the psoId for this authUid
            const currentPso = psoList.find(p => p.id === psoAuthUid);
            if (currentPso && currentPso.psoId) {
                allowedPsoIds = [currentPso.psoId];
            } else {
                // Fallback: use authUid directly
                allowedPsoIds = [psoAuthUid];
            }
        }
    }

    // Fetch branches from banks/{bankId}/branch
    const branchSnap = await getDocs(collection(db, "banks", bankId, "branch"));
    const branchList: Branch[] = [];

    branchSnap.forEach((doc) => {
        const branchData = doc.data();
        
        // Filter branches based on user role
        if (!isBankUser && allowedPsoIds !== null) {
            // For PSO users, only show branches with matching psoId
            if (!branchData.psoId || !allowedPsoIds.includes(branchData.psoId)) {
                return; // Skip this branch
            }
        }

        branchList.push({
            id: doc.id,
            bankId: bankId!,
            ...branchData,
            name: bankData.companyName || bankData.name || "N/A",
            locationName: locList.find(l => l.id === branchData.locationId)?.name || "N/A",
            psoName: psoList.find(p => p.psoId === branchData.psoId || p.id === branchData.psoId)?.name || "N/A",
            psoEmail: psoList.find(p => p.psoId === branchData.psoId || p.id === branchData.psoId)?.email || "N/A",
            psoContact: psoList.find(p => p.psoId === branchData.psoId || p.id === branchData.psoId)?.contact || "N/A",
        } as Branch);
    });

    // Sort by branch code
    branchList.sort((a, b) => {
        const codeA = a.branchCode || '';
        const codeB = b.branchCode || '';
        return codeA.localeCompare(codeB);
    });

    // Debug logging
    console.log('Fetched Managers:', Object.keys(managersMap).length, managersMap);
    console.log('Fetched Branches:', branchList.length);
    console.log('Sample Branch with Manager IDs:', branchList[0] ? {
        branchCode: branchList[0].branchCode,
        branchManagerId: branchList[0].branchManagerId,
        operationalManagerId: branchList[0].operationalManagerId,
        areaManagerId: branchList[0].areaManagerId,
    } : 'No branches');

    return {
        branches: branchList,
        managers: managersMap,
    };
}

export async function fetchBranchBankId(uid: string): Promise<string | null> {
    const banksSnap = await getDocs(collection(db, "banks"));
    for (const bankDoc of banksSnap.docs) {
        const posDocRef = doc(db, "banks", bankDoc.id, "pos", uid);
        const posDoc = await getDoc(posDocRef);
        if (posDoc.exists()) {
            return bankDoc.id;
        }
    }
    return null;
}

